import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Search,
  Filter,
  Download,
  Printer,
  ArrowUpDown,
  Calendar,
  CreditCard,
  FileText,
  RefreshCcw,
  X,
  ChevronLeft,
  ChevronRight,
  Hash,
  User,
  DollarSign,
  Clock,
  Tag,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import "../css/CollegePaymentsRecords.css";
import { jsPDF } from "jspdf";

function CollegePaymentsRecords() {
  // State variables
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPayments, setTotalPayments] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentMethods] = useState([
    "cash",
    "bank",
    "online",
    "gpay",
    "phonepe",
  ]);
  const [statuses] = useState([
    "generated",
    "printed",
    "downloaded",
    "emailed",
  ]);
  const [expandedReceipt, setExpandedReceipt] = useState(null);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });

  // Filter state
  const [filters, setFilters] = useState({
    search: "",
    rollNo: "",
    fromDate: "",
    toDate: "",
    paymentMethod: "",
    minAmount: "",
    maxAmount: "",
    academicYear: new Date().getFullYear().toString(),
    status: "",
  });

  // Sort state
  const [sortConfig, setSortConfig] = useState({
    key: "receiptDate",
    direction: "desc",
  });

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const rowsPerPage = 10;

  // Fetch payments based on filters and pagination
  useEffect(() => {
    fetchPayments();
  }, [currentPage, sortConfig]);

  // Hide notification after 3 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      // Build query string from filters
      const queryParams = new URLSearchParams();

      // Add search to both name and rollNo if present
      if (filters.search) {
        queryParams.append("search", filters.search);
      }

      if (filters.rollNo) queryParams.append("rollNo", filters.rollNo);
      if (filters.fromDate) queryParams.append("fromDate", filters.fromDate);
      if (filters.toDate) queryParams.append("toDate", filters.toDate);
      if (filters.paymentMethod)
        queryParams.append("paymentMethod", filters.paymentMethod);
      if (filters.minAmount) queryParams.append("minAmount", filters.minAmount);
      if (filters.maxAmount) queryParams.append("maxAmount", filters.maxAmount);
      if (filters.academicYear)
        queryParams.append("academicYear", filters.academicYear);
      if (filters.status) queryParams.append("status", filters.status);

      // Add pagination
      queryParams.append("page", currentPage);
      queryParams.append("limit", rowsPerPage);

      // Add sorting
      queryParams.append("sortBy", sortConfig.key);
      queryParams.append("sortDirection", sortConfig.direction);

      const response = await axios.get(
        `https://payroll-api.uniums.com/api/college/receipts?${queryParams.toString()}`
      );

      setPayments(response.data.receipts);
      setTotalPayments(response.data.count);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching payment records:", err);
      setError("Failed to load payment records. Please try again.");
      setLoading(false);
    }
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  // Apply filters
  const applyFilters = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when filtering
    fetchPayments();
    if (window.innerWidth < 768) {
      setShowFilters(false); // Hide filters panel on mobile after applying
    }

    // Show a notification
    setNotification({
      show: true,
      message: "Filters applied successfully",
      type: "success",
    });
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      search: "",
      rollNo: "",
      fromDate: "",
      toDate: "",
      paymentMethod: "",
      minAmount: "",
      maxAmount: "",
      academicYear: new Date().getFullYear().toString(),
      status: "",
    });
    setCurrentPage(1);

    // Don't fetch here, let the user click Apply
    setNotification({
      show: true,
      message: "Filters reset",
      type: "info",
    });
  };

  // Handle sorting
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Handle pagination
  const totalPages = Math.ceil(totalPayments / rowsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // View receipt details
  const toggleReceiptDetails = (receiptId) => {
    if (expandedReceipt === receiptId) {
      setExpandedReceipt(null);
    } else {
      setExpandedReceipt(receiptId);
    }
  };

  // Print receipt
  const printReceipt = async (receiptNo, e) => {
    e.stopPropagation();
    try {
      setNotification({
        show: true,
        message: "Preparing receipt for printing...",
        type: "info",
      });

      // Call the API to log the action
      await axios.post("https://payroll-api.uniums.com/api/college/log-receipt-action", {
        receiptNo,
        actionType: "print",
      });

      // Get the receipt data for printing
      const response = await axios.get(
        `https://payroll-api.uniums.com/api/college/receipt/${receiptNo}`
      );
      const receiptData = response.data.receipt;

      // Create a new window for printing
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
          <head>
            <title>Receipt #${receiptNo}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                max-width: 800px;
                margin: 0 auto;
              }
              .receipt-header {
                text-align: center;
                margin-bottom: 20px;
                border-bottom: 2px solid #333;
                padding-bottom: 10px;
              }
              .college-name {
                font-size: 24px;
                font-weight: bold;
                margin-bottom: 5px;
              }
              .receipt-title {
                font-size: 20px;
                margin: 20px 0;
                text-align: center;
              }
              .receipt-no {
                font-size: 16px;
                text-align: right;
                margin-bottom: 20px;
              }
              .receipt-details {
                margin-bottom: 20px;
              }
              .detail-row {
                display: flex;
                margin-bottom: 10px;
              }
              .label {
                font-weight: bold;
                width: 150px;
              }
              .value {
                flex: 1;
              }
              .amount-section {
                margin: 20px 0;
                border-top: 1px solid #ccc;
                border-bottom: 1px solid #ccc;
                padding: 15px 0;
              }
              .amount-row {
                display: flex;
                justify-content: space-between;
                font-size: 18px;
                font-weight: bold;
              }
              .footer {
                margin-top: 40px;
                text-align: center;
                font-size: 12px;
                color: #666;
              }
              .signature {
                margin-top: 50px;
                display: flex;
                justify-content: space-between;
              }
              .signature div {
                width: 200px;
                text-align: center;
              }
              .signature-line {
                border-top: 1px solid #333;
                margin-bottom: 5px;
              }
              @media print {
                body {
                  margin: 0;
                  padding: 15px;
                }
                button {
                  display: none;
                }
              }
            </style>
          </head>
          <body>
            <div class="receipt-header">
              <div class="college-name">Mayurbhanj College of Accountancy and Management</div>
               <p>Official Website: https://www.collegewebsite.com</p>
               <p>Address: Building, Campus | Baripada, Mayurbhanj, Odisha | Phone: 6732456782</p>
              <div>Phone: (+91) 67324-56782| Email: info@xyzcollege.edu</div>
            </div>
            
            <div class="receipt-title">PAYMENT RECEIPT</div>
            <div class="receipt-no">Receipt No: ${receiptNo}</div>
            
            <div class="receipt-details">
              <div class="detail-row">
                <div class="label">Student Name:</div>
                <div class="value">${receiptData?.student?.name || "N/A"}</div>
              </div>
              <div class="detail-row">
                <div class="label">Roll No:</div>
                <div class="value">${receiptData?.rollNo || "N/A"}</div>
              </div>
              <div class="detail-row">
                <div class="label">Date:</div>
                <div class="value">${formatDate(receiptData?.receiptDate)}</div>
              </div>
              <div class="detail-row">
                <div class="label">Academic Year:</div>
                <div class="value">${receiptData?.academicYear || "N/A"}</div>
              </div>
              <div class="detail-row">
                <div class="label">Payment Method:</div>
                <div class="value">${receiptData?.paymentMethod?.toUpperCase() || "N/A"
        }</div>
              </div>
            </div>
            
            <div class="amount-section">
              <div class="detail-row">
                <div class="label">Payment Title:</div>
                <div class="value">${receiptData?.title || "N/A"}</div>
              </div>
              <div class="amount-row">
                <div>Total Amount:</div>
                <div>₹${receiptData?.amount?.toLocaleString() || "0"}</div>
              </div>
            </div>
            
            <div class="detail-row">
              <div class="label">Remarks:</div>
              <div class="value">${receiptData?.remarks || "N/A"}</div>
            </div>
            
            <div class="signature">
              <div>
                <div class="signature-line"></div>
                <div>Received By</div>
              </div>
              <div>
                <div class="signature-line"></div>
                <div>Authorized Signature</div>
              </div>
            </div>
            
            <div class="footer">
              <p>This is a computer-generated receipt and does not require a signature.</p>
              <p>Thank you for your payment!</p>
            </div>
            
            <div style="text-align: center; margin-top: 20px;">
              <button onclick="window.print(); window.close();">Print Receipt</button>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();

      // Auto-trigger print
      setTimeout(() => {
        printWindow.print();
      }, 500);

      setNotification({
        show: true,
        message: "Receipt printed successfully",
        type: "success",
      });

      // Refresh the list to update status
      fetchPayments();
    } catch (err) {
      console.error("Error printing receipt:", err);
      setNotification({
        show: true,
        message: "Failed to print receipt",
        type: "error",
      });
    }
  };

  // Download receipt
  const downloadReceipt = async (receiptNo, e) => {
    e.stopPropagation();
    try {
      setNotification({
        show: true,
        message: "Preparing receipt for download...",
        type: "info",
      });

      // Call the API to log the action
      await axios.post("https://payroll-api.uniums.com/api/college/log-receipt-action", {
        receiptNo,
        actionType: "download",
      });

      // Get the receipt data
      const response = await axios.get(
        `https://payroll-api.uniums.com/api/college/receipt/${receiptNo}`
      );
      const receiptData = response.data.receipt;

      // Create a new PDF document
      const doc = new jsPDF();

      // Add content to the PDF
      // Header
      doc.setFontSize(18);
      doc.text("Mayurbhanj College of Accountancy and Management", 105, 20, { align: "center" });

      doc.setFontSize(10);
      doc.text("Official Website: https://www.collegewebsite.com", 105, 27, { align: "center" });
      doc.text("Address: Building, Campus | Baripada, Mayurbhanj, Odisha | Phone: 6732456782", 105, 32, { align: "center" });
      doc.text("Phone: (+91) 67324-56782| Email: info@xyzcollege.edu", 105, 37, { align: "center" });

      // Title
      doc.setFontSize(16);
      doc.text("PAYMENT RECEIPT", 105, 45, { align: "center" });

      // Receipt number
      doc.setFontSize(12);
      doc.text(`Receipt No: ${receiptNo}`, 150, 55, { align: "right" });

      // Student details
      doc.setFontSize(11);
      doc.text(`Student Name: ${receiptData?.student?.name || "N/A"}`, 20, 70);
      doc.text(`Roll No: ${receiptData?.rollNo || "N/A"}`, 20, 78);
      doc.text(`Date: ${formatDate(receiptData?.receiptDate)}`, 20, 86);
      doc.text(`Academic Year: ${receiptData?.academicYear || "N/A"}`, 20, 94);
      doc.text(`Payment Method: ${receiptData?.paymentMethod?.toUpperCase() || "N/A"}`, 20, 102);

      // Payment details
      doc.text(`Payment Title: ${receiptData?.title || "N/A"}`, 20, 118);

      // Amount
      doc.setFontSize(12);
      doc.text(`Total Amount: ₹${receiptData?.amount?.toLocaleString() || "0"}`, 150, 118, { align: "right" });

      // Draw horizontal lines
      doc.line(20, 110, 190, 110); // Line above payment details
      doc.line(20, 125, 190, 125); // Line below amount

      // Remarks
      doc.setFontSize(11);
      doc.text(`Remarks: ${receiptData?.remarks || "N/A"}`, 20, 140);

      // Signature spaces
      doc.line(40, 180, 80, 180); // Signature line 1
      doc.text("Received By", 60, 188, { align: "center" });

      doc.line(120, 180, 160, 180); // Signature line 2
      doc.text("Authorized Signature", 140, 188, { align: "center" });

      // Footer
      doc.setFontSize(9);
      doc.text("This is a computer-generated receipt and does not require a signature.", 105, 220, { align: "center" });
      doc.text("Thank you for your payment!", 105, 225, { align: "center" });

      // Save the PDF
      doc.save(`Receipt_${receiptNo}.pdf`);

      setNotification({
        show: true,
        message: "Receipt downloaded successfully",
        type: "success",
      });

      // Refresh the list to update status
      fetchPayments();
    } catch (err) {
      console.error("Error downloading receipt:", err);
      setNotification({
        show: true,
        message: "Failed to download receipt",
        type: "error",
      });
    }
  };

  // Helper to get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case "generated":
        return "payment-records-status-generated";
      case "printed":
        return "payment-records-status-printed";
      case "downloaded":
        return "payment-records-status-downloaded";
      case "emailed":
        return "payment-records-status-emailed";
      default:
        return "";
    }
  };

  // Helper to get payment method icon
  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case "cash":
        return <DollarSign size={16} />;
      case "bank":
        return <CreditCard size={16} />;
      case "online":
        return <CreditCard size={16} />;
      case "gpay":
        return <CreditCard size={16} />;
      case "phonepe":
        return <CreditCard size={16} />;
      default:
        return <CreditCard size={16} />;
    }
  };

  return (
    <div className="payment-records-container">
      {/* Notification */}
      {notification.show && (
        <div
          className={`payment-records-notification payment-records-notification-${notification.type}`}
        >
          {notification.type === "success" && <CheckCircle size={18} />}
          {notification.type === "error" && <AlertTriangle size={18} />}
          {notification.type === "info" && <FileText size={18} />}
          <p>{notification.message}</p>
        </div>
      )}

      <div className="payment-records-header">
        <h1>College Payment Records</h1>
        <div className="payment-records-actions">
          <div className="payment-records-search">
            <Search size={18} className="payment-records-search-icon" />
            <input
              type="text"
              placeholder="Search by name or roll number..."
              value={filters.search}
              name="search"
              onChange={handleFilterChange}
              className="payment-records-search-input"
            />
          </div>
          <button
            className="payment-records-filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
        </div>
      </div>

      {/* Filters panel */}
      <div
        className={`payment-records-filters ${showFilters ? "payment-records-filters-show" : ""
          }`}
      >
        <div className="payment-records-filters-header">
          <h2>Filters</h2>
          <button
            className="payment-records-close-filters"
            onClick={() => setShowFilters(false)}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={applyFilters} className="payment-records-filter-form">
          <div className="payment-records-filter-grid">
            <div className="payment-records-filter-item">
              <label>Roll Number</label>
              <input
                type="text"
                name="rollNo"
                value={filters.rollNo}
                onChange={handleFilterChange}
                placeholder="Enter roll number"
              />
            </div>

            <div className="payment-records-filter-item">
              <label>From Date</label>
              <div className="payment-records-input-icon">
                <Calendar size={16} className="payment-records-icon" />
                <input
                  type="date"
                  name="fromDate"
                  value={filters.fromDate}
                  onChange={handleFilterChange}
                />
              </div>
            </div>

            <div className="payment-records-filter-item">
              <label>To Date</label>
              <div className="payment-records-input-icon">
                <Calendar size={16} className="payment-records-icon" />
                <input
                  type="date"
                  name="toDate"
                  value={filters.toDate}
                  onChange={handleFilterChange}
                />
              </div>
            </div>

            <div className="payment-records-filter-item">
              <label>Payment Method</label>
              <select
                name="paymentMethod"
                value={filters.paymentMethod}
                onChange={handleFilterChange}
              >
                <option value="">All Methods</option>
                {paymentMethods.map((method) => (
                  <option key={method} value={method}>
                    {method.charAt(0).toUpperCase() + method.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="payment-records-filter-item">
              <label>Min Amount (₹)</label>
              <div className="payment-records-input-icon">
                <DollarSign size={16} className="payment-records-icon" />
                <input
                  type="number"
                  name="minAmount"
                  value={filters.minAmount}
                  onChange={handleFilterChange}
                  placeholder="Min"
                />
              </div>
            </div>

            <div className="payment-records-filter-item">
              <label>Max Amount (₹)</label>
              <div className="payment-records-input-icon">
                <DollarSign size={16} className="payment-records-icon" />
                <input
                  type="number"
                  name="maxAmount"
                  value={filters.maxAmount}
                  onChange={handleFilterChange}
                  placeholder="Max"
                />
              </div>
            </div>

            <div className="payment-records-filter-item">
              <label>Academic Year</label>
              <select
                name="academicYear"
                value={filters.academicYear}
                onChange={handleFilterChange}
              >
                <option value="">All Years</option>
                {[...Array(5)].map((_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year.toString()}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="payment-records-filter-item">
              <label>Status</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Statuses</option>
                {statuses.map((status) => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="payment-records-filter-actions">
            <button
              type="button"
              className="payment-records-reset-btn"
              onClick={resetFilters}
            >
              <RefreshCcw size={16} />
              Reset
            </button>
            <button type="submit" className="payment-records-apply-btn">
              <Filter size={16} />
              Apply Filters
            </button>
          </div>
        </form>
      </div>

      {/* Results stats */}
      <div className="payment-records-stats">
        <p>
          Showing{" "}
          <span className="payment-records-stats-highlight">
            {payments.length}
          </span>{" "}
          of{" "}
          <span className="payment-records-stats-highlight">
            {totalPayments}
          </span>{" "}
          payment records
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="payment-records-error">
          <p>{error}</p>
          <button onClick={fetchPayments}>Try Again</button>
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="payment-records-loading">
          <div className="payment-records-spinner"></div>
          <p>Loading payment records...</p>
        </div>
      ) : (
        <>
          {/* Payment records table */}
          {payments.length > 0 ? (
            <div className="payment-records-table-container">
              <table className="payment-records-table">
                <thead>
                  <tr>
                    <th
                      onClick={() => requestSort("receiptNo")}
                      className={
                        sortConfig.key === "receiptNo"
                          ? `payment-records-th-active payment-records-th-${sortConfig.direction}`
                          : ""
                      }
                    >
                      <div className="payment-records-th-content">
                        <Hash size={16} />
                        Receipt No
                        <ArrowUpDown size={14} />
                      </div>
                    </th>
                    <th
                      onClick={() => requestSort("student.name")}
                      className={
                        sortConfig.key === "student.name"
                          ? `payment-records-th-active payment-records-th-${sortConfig.direction}`
                          : ""
                      }
                    >
                      <div className="payment-records-th-content">
                        <User size={16} />
                        Student
                        <ArrowUpDown size={14} />
                      </div>
                    </th>
                    <th
                      onClick={() => requestSort("rollNo")}
                      className={
                        sortConfig.key === "rollNo"
                          ? `payment-records-th-active payment-records-th-${sortConfig.direction}`
                          : ""
                      }
                    >
                      <div className="payment-records-th-content">
                        <Hash size={16} />
                        Roll No
                        <ArrowUpDown size={14} />
                      </div>
                    </th>
                    <th
                      onClick={() => requestSort("title")}
                      className={
                        sortConfig.key === "title"
                          ? `payment-records-th-active payment-records-th-${sortConfig.direction}`
                          : ""
                      }
                    >
                      <div className="payment-records-th-content">
                        <Tag size={16} />
                        Title
                        <ArrowUpDown size={14} />
                      </div>
                    </th>
                    <th
                      onClick={() => requestSort("amount")}
                      className={
                        sortConfig.key === "amount"
                          ? `payment-records-th-active payment-records-th-${sortConfig.direction}`
                          : ""
                      }
                    >
                      <div className="payment-records-th-content">
                        <DollarSign size={16} />
                        Amount
                        <ArrowUpDown size={14} />
                      </div>
                    </th>
                    <th
                      onClick={() => requestSort("receiptDate")}
                      className={
                        sortConfig.key === "receiptDate"
                          ? `payment-records-th-active payment-records-th-${sortConfig.direction}`
                          : ""
                      }
                    >
                      <div className="payment-records-th-content">
                        <Clock size={16} />
                        Date
                        <ArrowUpDown size={14} />
                      </div>
                    </th>
                    <th
                      onClick={() => requestSort("status")}
                      className={
                        sortConfig.key === "status"
                          ? `payment-records-th-active payment-records-th-${sortConfig.direction}`
                          : ""
                      }
                    >
                      <div className="payment-records-th-content">
                        Status
                        <ArrowUpDown size={14} />
                      </div>
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <React.Fragment key={payment._id}>
                      <tr
                        className={
                          expandedReceipt === payment._id
                            ? "payment-records-row-active"
                            : ""
                        }
                        onClick={() => toggleReceiptDetails(payment._id)}
                      >
                        <td className="payment-records-receipt-no">
                          {payment.receiptNo}
                        </td>
                        <td>
                          {payment.student && (
                            <div className="payment-records-student">
                              <span className="payment-records-student-name">
                                {payment.student.name}
                              </span>
                              <span className="payment-records-student-email">
                                {payment.student.email}
                              </span>
                            </div>
                          )}
                        </td>
                        <td>{payment.rollNo}</td>
                        <td>{payment.title}</td>
                        <td className="payment-records-amount">
                          ₹{payment.amount.toLocaleString()}
                        </td>
                        <td>{formatDate(payment.receiptDate)}</td>
                        <td>
                          <span
                            className={`payment-records-status ${getStatusColor(
                              payment.status
                            )}`}
                          >
                            {payment.status}
                          </span>
                        </td>
                        <td>
                          <div className="payment-records-actions-cell">
                            <button
                              className="payment-records-action-btn payment-records-print-btn"
                              onClick={(e) =>
                                printReceipt(payment.receiptNo, e)
                              }
                              title="Print Receipt"
                            >
                              <Printer size={16} />
                            </button>
                            <button
                              className="payment-records-action-btn payment-records-download-btn"
                              onClick={(e) =>
                                downloadReceipt(payment.receiptNo, e)
                              }
                              title="Download Receipt"
                            >
                              <Download size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded row with details */}
                      {expandedReceipt === payment._id && (
                        <tr className="payment-records-expanded-row">
                          <td colSpan="8">
                            <div className="payment-records-details">
                              <div className="payment-records-details-grid">
                                <div className="payment-records-detail-item">
                                  <h4>Payment Method</h4>
                                  <div className="payment-records-detail-value payment-records-method">
                                    {getPaymentMethodIcon(
                                      payment.paymentMethod
                                    )}
                                    <span>
                                      {payment.paymentMethod
                                        .charAt(0)
                                        .toUpperCase() +
                                        payment.paymentMethod.slice(1)}
                                    </span>
                                  </div>
                                </div>

                                <div className="payment-records-detail-item">
                                  <h4>Academic Year</h4>
                                  <div className="payment-records-detail-value">
                                    {payment.academicYear}
                                  </div>
                                </div>

                                <div className="payment-records-detail-item">
                                  <h4>Remarks</h4>
                                  <div className="payment-records-detail-value">
                                    {payment.remarks || "No remarks"}
                                  </div>
                                </div>

                                {payment.paymentMethod === "bank" &&
                                  payment.bankDetails && (
                                    <div className="payment-records-detail-item payment-records-bank-details">
                                      <h4>Bank Details</h4>
                                      <div className="payment-records-detail-value">
                                        <p>
                                          Account:{" "}
                                          {payment.bankDetails.accountName ||
                                            "N/A"}
                                        </p>
                                        <p>
                                          Bank:{" "}
                                          {payment.bankDetails.bankName ||
                                            "N/A"}
                                        </p>
                                        <p>
                                          IFSC:{" "}
                                          {payment.bankDetails.ifscCode ||
                                            "N/A"}
                                        </p>
                                      </div>
                                    </div>
                                  )}

                                {(payment.paymentMethod === "gpay" ||
                                  payment.paymentMethod === "phonepe") &&
                                  payment.upiId && (
                                    <div className="payment-records-detail-item">
                                      <h4>UPI ID</h4>
                                      <div className="payment-records-detail-value">
                                        {payment.upiId}
                                      </div>
                                    </div>
                                  )}

                                <div className="payment-records-detail-item payment-records-actions-log">
                                  <h4>Actions Log</h4>
                                  <div className="payment-records-detail-value">
                                    {payment.actions &&
                                      payment.actions.length > 0 ? (
                                      <ul className="payment-records-actions-list">
                                        {payment.actions.map(
                                          (action, index) => (
                                            <li key={index}>
                                              <span className="payment-records-action-type">
                                                {action.type}
                                              </span>
                                              <span className="payment-records-action-time">
                                                {new Date(
                                                  action.timestamp
                                                ).toLocaleString()}
                                              </span>
                                            </li>
                                          )
                                        )}
                                      </ul>
                                    ) : (
                                      <p>No actions recorded</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="payment-records-empty">
              <FileText size={48} />
              <h3>No payment records found</h3>
              <p>Try adjusting your search or filter criteria</p>
              <button
                onClick={resetFilters}
                className="payment-records-reset-search"
              >
                Reset Filters
              </button>
            </div>
          )}

          {/* Pagination */}
          {payments.length > 0 && (
            <div className="payment-records-pagination">
              <button
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
                className="payment-records-page-btn"
              >
                First
              </button>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="payment-records-page-btn"
              >
                <ChevronLeft size={16} />
                Prev
              </button>

              <div className="payment-records-page-numbers">
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  // Calculate page numbers to show around current page
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  if (pageNum > 0 && pageNum <= totalPages) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum)}
                        className={`payment-records-page-num ${currentPage === pageNum
                          ? "payment-records-current-page"
                          : ""
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="payment-records-page-btn"
              >
                Next
                <ChevronRight size={16} />
              </button>
              <button
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages}
                className="payment-records-page-btn"
              >
                Last
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default CollegePaymentsRecords;
